import React, { useMemo } from 'react'
import { Navigate, useLocation, useSearchParams } from 'react-router-dom'

import { paths } from 'src/config/paths'
import { useAuthState } from 'src/shared/features/auth/api/use-auth-state'
import { useToast } from 'src/shared/hooks/use-toast'

interface AuthGuardProps {
  children: React.ReactNode
  requireAuth?: boolean
  allowUnauthenticatedPaths?: string[]
}

const Guard = ({ children }: AuthGuardProps) => {
  const { isSignedIn } = useAuthState()
  const location = useLocation()

  return useMemo(() => {
    if (!isSignedIn && ![paths.auth.login.path, paths.auth.signup.path].includes(location.pathname as any)) {
      // Create a full URL that preserves both pathname and search params
      const redirectUrl = location.pathname + (location.search || '')
      return <Navigate to={paths.auth.login.getHref(redirectUrl)} replace />
    }

    return <>{children}</>
  }, [isSignedIn, location.pathname, location.search, children])
}

export const AuthGuard = ({ children }: AuthGuardProps) => {
  return <Guard>{children}</Guard>
}

export const ConsumerAuthGuard = ({ children }: AuthGuardProps) => {
  const { isConsumerSignedIn } = useAuthState()

  const location = useLocation()
  const { toast } = useToast()

  return useMemo(() => {
    if (!isConsumerSignedIn) {
      // Create a full URL that preserves both pathname and search params
      const redirectUrl = location.pathname + (location.search || '')
      return <Navigate to={paths.consumer.auth.loginPhone.getHref(redirectUrl)} replace />
    }

    return <>{children}</>
  }, [isConsumerSignedIn, location.pathname, location.search, children, toast])
}

export const EnterpriseAuthGuard = ({ children }: AuthGuardProps) => {
  const { isSignedIn } = useAuthState()

  const location = useLocation()
  const { toast } = useToast()

  return useMemo(() => {
    if (!isSignedIn) {
      toast({
        title: 'You must be signed in to access this page',
        variant: 'destructive',
        duration: 5000,
      })
      // Create a full URL that preserves both pathname and search params
      const redirectUrl = location.pathname + (location.search || '')
      return <Navigate to={paths.enterprise.login.getHref(redirectUrl)} replace />
    }

    return <>{children}</>
  }, [isSignedIn, location.pathname, location.search, children, toast])
}

/** Guard to prevent access to enterprise signin, signup, confirm-signup, and login-existing pages if the user is already signed in. */
export const EnterpriseSignInFlowGuard = ({ children }: AuthGuardProps) => {
  const { isSignedIn } = useAuthState()
  const [searchParams] = useSearchParams()
  const redirectTo = searchParams.get('redirectTo')

  return useMemo(() => {
    if (isSignedIn) {
      return <Navigate to={redirectTo ? redirectTo : paths.enterprise.dashboard.path} replace />
    }

    return <>{children}</>
  }, [isSignedIn, children])
}
